import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader, SectionPad } from "../../components/ui-extras";
import { Link } from "react-router-dom";
import MixedCountryCharts from "../../components/MixedCountryCharts";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
// P4-N5B Slice C5 R1 — canonical Network/Country presentation catalog
// (C1 foundation). NOTE: Dashboard is a STRICT backend (dashboard.py
// does NOT widen `network=esso → {esso, ta_petro}`). C1's generic
// `toWireParams` is therefore NOT SAFE for the Esso+USA case here —
// this file uses a local `dashboardCanonicalToWire` compatibility
// adapter (§C5 §10) that maps only Esso+US → `network=ta_petro&
// country=US`. All other canonical pairs pass through identity.
import {
  COUNTRY_LABELS,
  TRANSACTION_CAPABILITY,
  getVisibleCountries,
} from "../../lib/networkCountryCatalog";

// "All networks" branch — union of every provider's transaction-country
// capability. Consumes C1's TRANSACTION_CAPABILITY matrix directly.
const DASHBOARD_ALL_NETWORKS_COUNTRIES = (() => {
  const union = Array.from(new Set(Object.values(TRANSACTION_CAPABILITY).flat()));
  return union.length > 1 ? ["", ...union] : union;
})();

function _dashboardCountriesFor(network) {
  if (!network) return DASHBOARD_ALL_NETWORKS_COUNTRIES;
  return getVisibleCountries("transactions", network);
}

// P4-N5B Slice C5 R1 — Dashboard-specific canonical presentation → wire
// adapter. Handles the ONE case where Dashboard semantics diverge from
// C1's transaction wire model: Esso+USA. Every OTHER pair passes
// through identity. This adapter is intentionally LOCAL to Dashboard.jsx
// (not added to C1) to keep C1 pure and avoid coupling other transaction
// surfaces to Dashboard's strict-match backend behaviour.
function dashboardCanonicalToWire(network, country) {
  if (network === "esso" && country === "US") {
    return { network: "ta_petro", country: "US" };
  }
  return { network: network || null, country: country || null };
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  // Operations Overview filters (Jun 18 2026 executive simplification).
  //   • Customer  — admin-only narrow-down. Empty = all customers.
  //   • From / To — YYYY-MM-DD inclusive; empty = backend's 30d default.
  //   • Network   — esso / pipeline / ta_petro / "" (= both)
  //   • Country   — CA / US, only meaningful when Network=esso.
  const [customerId, setCustomerId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [network, setNetwork] = useState("");
  const [country, setCountry] = useState("");
  // bump on Refresh button to force the stats useEffect to re-fetch.
  const [refreshTick, setRefreshTick] = useState(0);

  // Load the customer dropdown once on mount. Sorted alphabetically by
  // company_name for fast scanning. Backend already returns admins'
  // full list; non-admin portal users won't render this dropdown.
  useEffect(() => {
    api.get("/customers").then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => (a.company_name || "").localeCompare(b.company_name || ""));
      setCustomers(list);
    }).catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    const params = {};
    if (dateFrom && dateTo) { params.date_from = dateFrom; params.date_to = dateTo; }
    // P4-N5B Slice C5 R1 — canonical wire construction via the local
    // Dashboard-only adapter. `network` state stores ONLY canonical
    // providers ({"", "esso", "pipeline", "petro_pass"}). Esso+USA is
    // mapped internally to `network=ta_petro&country=US` because
    // Dashboard backend (routes/dashboard.py:84) does NOT widen — Esso
    // USA rows are persisted exclusively as `ta_petro/US` at import time.
    const wire = dashboardCanonicalToWire(network, country);
    if (wire.network) params.network = wire.network;
    if (wire.country) params.country = wire.country;
    if (customerId) params.customer_id = customerId;
    api.get("/dashboard/stats", { params }).then(({ data }) => setStats(data)).catch(() => {});
  }, [dateFrom, dateTo, network, country, customerId, refreshTick]);

  // Preset helpers — common ranges users actually need.
  const setRange = (days) => {
    const today = new Date();
    const past = new Date();
    past.setDate(today.getDate() - days + 1);  // inclusive
    setDateFrom(past.toISOString().slice(0, 10));
    setDateTo(today.toISOString().slice(0, 10));
  };
  const setSingleDay = () => {
    const today = new Date().toISOString().slice(0, 10);
    setDateFrom(today);
    setDateTo(today);
  };

  // Volume in L with 2-decimal precision for executive readability —
  // matches the operator's reconciliation workbook format
  // (25,553.98 L, not 25,554 L).

  // Currency resolver (RCA 2026-07-03) — figures out which currency the
  // KPI tiles are showing given the current filter set. USD transactions
  // must stay USD (never converted to CAD), so we label each tile
  // explicitly instead of leaving the $-prefix ambiguous.
  //
  //   • network=ta_petro                       → USD (TA Petro is USA-only)
  //   • network=esso AND country=US            → USD (ESSO USA)
  //   • network=esso AND (country=CA or empty) → CAD
  //   • network=pipeline                       → CAD
  //   • network="" (Both networks)             → MIXED (CAD + USD summed)
  //
  // The MIXED case surfaces a warning banner because the aggregator sums
  // dollar values across currencies without conversion — the operator
  // should pick a specific network for a clean single-currency read.
  // P4-N5B Slice C5 R1 — post-C5 correct MIXED-currency trigger. The
  // pre-C5 logic fired MIXED whenever `network === ""` regardless of
  // country, incorrectly flagging All+Canada and All+USA (which are
  // in fact single-currency reads). Post-C5 MIXED fires ONLY when
  // BOTH network AND country are empty — the sole selection that can
  // aggregate CAD + USD rows without conversion.
  //
  //   • network=esso    country=US → USD
  //   • network=esso    country=CA or "" → CAD (production has zero esso/US rows)
  //   • network=pipeline → CAD
  //   • network=petro_pass country=US → USD
  //   • network=petro_pass country=CA or "" → CAD (petro_pass empty-country → backend zeros)
  //   • network=""      country=CA → CAD (NEW single-currency safety)
  //   • network=""      country=US → USD (NEW single-currency safety)
  //   • network=""      country=""  → MIXED (fires warning)

  return (
    <SectionPad>
      <PageHeader
        title="Operations Overview"
        subtitle="DASHBOARD"
        testId="admin-dashboard-header"
        actions={
          <div className="flex items-center gap-2">
            <button
              className="gf-btn gf-btn-ghost"
              onClick={() => setRefreshTick((n) => n + 1)}
              data-testid="dashboard-refresh"
              title="Re-fetch the four KPI tiles with the currently selected filters."
            >
              Refresh
            </button>
            <Link to="/transactions" className="gf-btn gf-btn-outline" data-testid="dashboard-go-transactions">
              Open transactions
            </Link>
          </div>
        }
      />

      {/* Filter strip — drives all 4 KPI tiles + the chart below. */}
      <div className="gf-card mb-6" data-testid="dashboard-date-filter">
        <div className="gf-card-body flex flex-wrap items-end gap-3">
          <div className="min-w-[220px]">
            <div className="overline mb-1">CUSTOMER</div>
            <select
              className="gf-input w-full"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              data-testid="dashboard-customer-filter"
            >
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.customer_id} value={c.customer_id}>{c.company_name}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="overline mb-1">FROM</div>
            <input
              type="date"
              className="gf-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="dashboard-date-from"
            />
          </div>
          <div>
            <div className="overline mb-1">TO</div>
            <input
              type="date"
              className="gf-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="dashboard-date-to"
            />
          </div>
          <div className="flex items-center gap-2">
            <button className="gf-btn gf-btn-ghost text-xs" onClick={setSingleDay} data-testid="dashboard-preset-today">Today</button>
            <button className="gf-btn gf-btn-ghost text-xs" onClick={() => setRange(7)} data-testid="dashboard-preset-7d">7d</button>
            <button className="gf-btn gf-btn-ghost text-xs" onClick={() => setRange(30)} data-testid="dashboard-preset-30d">30d</button>
            <button className="gf-btn gf-btn-ghost text-xs" onClick={() => setRange(90)} data-testid="dashboard-preset-90d">90d</button>
            <button
              className="gf-btn gf-btn-ghost text-xs"
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              data-testid="dashboard-preset-clear"
            >Clear</button>
          </div>
          <div>
            <div className="overline mb-1">NETWORK</div>
            <select
              className="gf-input"
              value={network}
              onChange={(e) => {
                // P4-N5B Slice C5 R1 — canonical Network dropdown.
                // `network` state stores ONLY canonical providers
                // ({"", "esso", "pipeline", "petro_pass"}). Country is
                // reconciled via C1's capability helper.
                const nw = e.target.value;
                const validCountries = _dashboardCountriesFor(nw);
                const nextCountry = validCountries.includes(country)
                  ? country
                  : (validCountries.length === 1 ? validCountries[0] : "");
                setNetwork(nw);
                setCountry(nextCountry);
              }}
              data-testid="dashboard-network-filter"
            >
              <option value="">All networks</option>
              <option value="esso">Esso</option>
              <option value="pipeline">Pipeline</option>
              <option value="petro_pass">Petro-Pass</option>
            </select>
          </div>
          <div data-testid="dashboard-country-wrap">
            <div className="overline mb-1">COUNTRY</div>
            <select
              className="gf-input"
              value={country}
              disabled={_dashboardCountriesFor(network).length <= 1}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="dashboard-country-filter"
              title={network === "pipeline"
                ? "Pipeline is Canada-only."
                : "Filter Dashboard aggregates by country. All+All keeps every country visible and shows the multi-currency warning."}
            >
              {/* P4-N5B Slice C5 R1 — Country options driven ENTIRELY
                  by the C1 capability matrix. Pipeline single-capability
                  lane locks the selector. */}
              {_dashboardCountriesFor(network).map((c) => (
                <option key={c || "all"} value={c}>{COUNTRY_LABELS[c]}</option>
              ))}
            </select>
          </div>
          <div className="ml-auto text-xs text-zinc-500 font-mono" data-testid="dashboard-window-label">
            {dateFrom && dateTo
              ? `${dateFrom} → ${dateTo}`
              : "Default · last 30d"}
            <span className="ml-2">· {
              // P4-N5B Slice C5 R1 — canonical window-label. No "Both
              // networks" or compound legacy labels. Presentation only.
              (network === "" && country === "")   ? "ALL NETWORKS / ALL COUNTRIES"
              : (network === "" && country === "CA") ? "ALL NETWORKS / CANADA"
              : (network === "" && country === "US") ? "ALL NETWORKS / USA"
              : (network === "esso" && country === "CA")       ? "ESSO / CANADA"
              : (network === "esso" && country === "US")       ? "ESSO / USA"
              : (network === "esso")                            ? "ESSO"
              : (network === "pipeline")                        ? "PIPELINE / CANADA"
              : (network === "petro_pass" && country === "CA") ? "PETRO-PASS / CANADA"
              : (network === "petro_pass" && country === "US") ? "PETRO-PASS / USA"
              : (network === "petro_pass")                      ? "PETRO-PASS"
              : network.toUpperCase()
            }</span>
          </div>
        </div>
      </div>

      {/* Dashboard R2 — transaction-only totals, one block per lane (never mixes CAD/USD or L/GAL). */}
      <OperationalLanes operational={stats?.operational} />

      {/* Phase 3E-9N — Geographic analytics: Province/State → City drilldown.
          When the current filter yields Canada-only data → Province chart only.
          USA-only → State chart only. Both → two independent panels (litres
          stay in the Canada panel, gallons stay in the USA panel). Filter
          changes reset stale drilldown selections automatically. */}
      {customerId ? (
        <MixedCountryCharts
          customerId={customerId}
          network={network || undefined}
          country={country || undefined}
          defaultDays={dateFrom && dateTo ? undefined : 90}
        />
      ) : (
        <div className="gf-card mb-6" data-testid="fueling-jurisdiction-hint">
          <div className="gf-card-body text-sm text-zinc-500 font-mono">
            Pick a customer above to see the {network === "ta_petro" || country === "US"
              ? "State → City" : network === "esso" || network === "pipeline" || country === "CA"
              ? "Province → City" : "Province / State → City"} geographic breakdown.
          </div>
        </div>
      )}

      {/* Charts and tables below the KPI strip remain for operational
          context (operator spec: "Keep charts below the KPI section").
          Drill-down modal removed from this view — for per-transaction
          inspection use the Transactions page or the per-card export. */}
    </SectionPad>
  );
}


// ---------------------------------------------------------------------------
// Executive KPI tile (Jun 18 2026) — single bold number, small subtitle,
// rounded corners, accent stripe on the left. No technical jargon.
// Used by the Operations Overview top strip; everything else on the
// page still uses the StatTile / MicroTile family for density.

const LANE_TITLES = { CA: "CANADA", US: "USA" };

function OperationalLanes({ operational }) {
  const lanes = ["CA", "US"].filter((l) => operational?.[l]);
  if (!operational) return null;
  if (!lanes.length) {
    return <div className="text-sm text-zinc-500 py-6" data-testid="op-no-transactions">No transactions for the selected filters.</div>;
  }
  return lanes.map((l) => <OperationalLane key={l} lane={l} t={operational[l]} />);
}

const NA_NUM = (v, d = 2) => Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

function PctSub({ pct, label, testId }) {
  return (
    <span>
      <span className="block font-mono text-base font-semibold text-zinc-800" data-testid={testId}>{NA_NUM(pct)}%</span>
      <span className="block mt-0.5">{label}</span>
    </span>
  );
}

function OperationalLane({ lane, t }) {
  const money = (v) => `${Number(v) < 0 ? "-" : ""}$${NA_NUM(Math.abs(Number(v || 0)))}`;
  const id = (k) => `op-${lane.toLowerCase()}-${k}`;
  return (
    <div className="mb-6" data-testid={`op-lane-${lane.toLowerCase()}`}>
      <div className="mb-2 mt-1 flex items-baseline gap-3">
        <div className="overline text-zinc-500 text-[11px] tracking-[0.22em]">{LANE_TITLES[lane]}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{t.currency} · {t.uom}</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiTile label="AMOUNT COLLECTIBLE WITH TAXES" value={money(t.amount_with_taxes)} sub="Σ FINAL AMT" testId={id("final-amt")} accent="emerald" compact />
        <KpiTile label="SUPPLIER PAYABLE WITH TAXES" value={money(t.supplier_payable)} sub="Σ supplier charges incl. taxes" testId={id("supplier")} accent="zinc" compact />
        <KpiTile label="GROSS PROFIT (WITH TAXES)" value={money(t.gross_profit_with_taxes)} testId={id("gp-tax")} accent="indigo" compact
                 sub={<PctSub pct={t.gp_pct_with_tax} label="Profit % of Supplier Cost — With Tax" testId={id("gp-tax-pct")} />} />
        <KpiTile label="AMOUNT COLLECTIBLE WITHOUT TAXES" value={money(t.amount_without_taxes)} sub="Σ AMT" testId={id("amt")} accent="emerald" compact />
        <KpiTile label="SUPPLIER PAYABLE WITHOUT TAXES" value={money(t.supplier_payable_without_taxes)} sub="Σ supplier charges before sales taxes" testId={id("supplier-pretax")} accent="zinc" compact />
        <KpiTile label="GROSS PROFIT (WITHOUT TAXES)" value={money(t.gross_profit)} testId={id("gp")} accent="indigo" primary compact
                 sub={<PctSub pct={t.gp_pct_before_tax} label="Profit % of Supplier Cost — Before Tax" testId={id("gp-pct")} />} />
      </div>
      <LaneGpChart lane={lane} t={t} money={money} />
      <RegionSummary lane={lane} t={t} money={money} />
    </div>
  );
}

function LaneGpChart({ lane, t, money }) {
  return (
    <div className="gf-card mt-4" data-testid={`op-${lane.toLowerCase()}-gp-chart`}>
      <div className="gf-card-header">
        <h3>Gross profit (without taxes) — {LANE_TITLES[lane]}</h3>
        <div className="overline">{t.currency} · DAILY</div>
      </div>
      <div className="gf-card-body" style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={t.daily_gp || []}>
            <CartesianGrid stroke="#E4E4E7" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#71717a" />
            <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#71717a" />
            <Tooltip contentStyle={{ border: "1px solid #09090b", borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 11 }}
                     formatter={(v) => [`${t.currency} ${money(v)}`, "GP"]} />
            <Bar dataKey="gp" name="GP" fill={lane === "US" ? "#2563eb" : "#dc2626"} maxBarSize={48} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function RegionSummary({ lane, t, money }) {
  const id = `op-${lane.toLowerCase()}-regions`;
  return (
    <div className="gf-card mt-4" data-testid={id}>
      <div className="gf-card-header">
        <h3>By {lane === "US" ? "state" : "province"} — {LANE_TITLES[lane]}</h3>
        <div className="overline">{t.currency} · {t.uom}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="gf-table min-w-[860px]" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "12%" }} /><col style={{ width: "8%" }} /><col style={{ width: "14%" }} />
            <col style={{ width: "16%" }} /><col style={{ width: "16%" }} /><col style={{ width: "18%" }} /><col style={{ width: "16%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>{lane === "US" ? "STATE" : "PROVINCE"}</th>
              {["TX", `VOLUME (${t.uom})`, "AMT", "FINAL AMT", "SUPPLIER PAYABLE", "GP (W/O TAX)"].map((h) => (
                <th key={h} style={{ textAlign: "right" }} className="whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...(t.regions || []).map((r) => ({ ...r, key: r.region, label: r.region })),
              { ...t, key: "__total", label: "TOTAL" }].map((r) => (
              <tr key={r.key} className={r.key === "__total" ? "font-semibold" : ""}
                  data-testid={r.key === "__total" ? `${id}-total` : `${id}-row-${r.region}`}>
                <td className="font-mono whitespace-nowrap" style={{ textAlign: "left" }}>{r.label}</td>
                {[NA_NUM(r.count, 0), NA_NUM(r.volume), money(r.amount_without_taxes), money(r.amount_with_taxes),
                  money(r.supplier_payable), money(r.gross_profit)].map((v, i) => (
                  <td key={i} className="gf-num whitespace-nowrap">{v}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiTile({ label, value, sub, testId, accent = "zinc", primary = false, compact = false }) {
  // Left-edge accent + readable type scale. Accent palette tuned to
  // sit on the existing zinc-50 page background without competing
  // with the brand red.
  const accentClass = {
    emerald: "before:bg-emerald-500",
    indigo:  "before:bg-indigo-500",
    amber:   "before:bg-amber-500",
    zinc:    "before:bg-zinc-700",
  }[accent] || "before:bg-zinc-700";
  return (
    <div
      data-testid={testId}
      className={
        "relative gf-card overflow-hidden " +
        "before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 " +
        accentClass + " " +
        (primary ? "ring-1 ring-indigo-200 shadow-md" : "shadow-sm") +
        " hover:shadow-md transition rounded-lg"
      }
    >
      <div className="gf-card-body pl-5 py-6">
        <div className="overline text-zinc-500 text-[11px] tracking-[0.18em]">{label}</div>
        <div
          className={
            "mt-3 font-mono font-semibold leading-none " +
            (compact ? "text-base sm:text-2xl lg:text-3xl whitespace-nowrap " : primary ? "text-4xl md:text-[2.6rem] " : "text-3xl md:text-4xl ") +
            (primary ? "text-indigo-700" : "text-zinc-900")
          }
          data-testid={`${testId}-value`}
        >
          {value}
        </div>
        {sub && (
          <div className="mt-3 text-xs text-zinc-500">{sub}</div>
        )}
      </div>
    </div>
  );
}
